import { useCallback, useEffect, useRef, useState } from 'react';

/** localStorage key to track install prompt dismissal */
const DISMISS_KEY = 'pwa-install-dismissed-at';

/** Don't re-show the prompt for 30 days after dismissal */
const DISMISS_DURATION_MS = 30 * 24 * 60 * 60 * 1000;

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: readonly string[];
  prompt(): Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PwaInstallState {
  /** Whether the install prompt can be shown */
  readonly canInstall: boolean;
  /** Whether the app is running as an installed PWA */
  readonly isInstalled: boolean;
  /** Whether the device is iOS (needs manual install instructions) */
  readonly isIos: boolean;
  /** Trigger the native install prompt (Chrome/Edge) */
  readonly promptInstall: () => Promise<boolean>;
  /** Dismiss the install prompt for 30 days */
  readonly dismissInstall: () => void;
}

function getIsIos(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in globalThis)
  );
}

function getIsInstalled(): boolean {
  return (
    globalThis.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in navigator &&
      (navigator as { standalone?: boolean }).standalone === true)
  );
}

function isDismissed(): boolean {
  const dismissedAt = localStorage.getItem(DISMISS_KEY);
  if (!dismissedAt) return false;
  const elapsed = Date.now() - Number(dismissedAt);
  return elapsed < DISMISS_DURATION_MS;
}

/**
 * Hook to manage PWA install prompt lifecycle.
 *
 * Captures the `beforeinstallprompt` event, tracks dismissal state
 * in localStorage (30-day cooldown), and detects iOS for manual
 * install instructions.
 */
export function usePwaInstall(): PwaInstallState {
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(getIsInstalled);
  const isIos = getIsIos();

  useEffect(() => {
    if (isInstalled || isDismissed()) return;

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      deferredPromptRef.current = e as BeforeInstallPromptEvent;
      setCanInstall(true);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setCanInstall(false);
      deferredPromptRef.current = null;
    };

    globalThis.addEventListener(
      'beforeinstallprompt',
      handleBeforeInstallPrompt
    );
    globalThis.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      globalThis.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      );
      globalThis.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isInstalled]);

  const promptInstall = useCallback(async (): Promise<boolean> => {
    const prompt = deferredPromptRef.current;
    if (!prompt) return false;

    const { outcome } = await prompt.prompt();
    deferredPromptRef.current = null;
    setCanInstall(false);

    return outcome === 'accepted';
  }, []);

  const dismissInstall = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setCanInstall(false);
    deferredPromptRef.current = null;
  }, []);

  return {
    canInstall: canInstall && !isInstalled && !isDismissed(),
    isInstalled,
    isIos,
    promptInstall,
    dismissInstall,
  };
}
